import { useMemo } from "react";
import { LineChart, Line, YAxis, Legend, ResponsiveContainer } from "recharts";
import { Thermometer } from "lucide-react";

/**
 * Minimalist hover preview shown ON TOP of a hovered body-map region. Two
 * labeled lines (pain in dark, stretching in amber), no axis lines, no ticks,
 * no numbers, no region name — deliberately austere so it reads at a glance
 * without cluttering the model. The full detailed graph is only revealed once
 * the region is clicked (popup above the model).
 */
export default function BodyRegionHoverChart({ area, byBodyPart, treatments, hasData }) {
  const base = area ? area.replace(/_(left|right)$/, "") : null;
  const series = useMemo(() => {
    const s = base && byBodyPart?.[base]?.series;
    return s || [];
  }, [base, byBodyPart]);
  const treat = base ? (treatments?.[base] || null) : null;

  if (!hasData || series.length === 0) {
    return (
      <div>
        <div className="text-[10px] text-zinc-500 italic py-2">
          No logs yet · click to open plan
        </div>
        {treat && <TreatmentLine treat={treat} />}
      </div>
    );
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={80}>
        <LineChart data={series} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
          {/* Hidden axes — we render the shape only, no numbers/no lines */}
          <YAxis yAxisId="pain" domain={[0, 10]} hide />
          <YAxis yAxisId="stretch" orientation="right" hide />
          <Legend
            verticalAlign="bottom"
            height={16}
            iconSize={8}
            wrapperStyle={{ fontSize: 10, fontWeight: 600, paddingTop: 2 }}
          />
          <Line
            yAxisId="pain"
            type="monotone"
            dataKey="pain"
            name="Pain"
            stroke="#0f172a"
            strokeWidth={2}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
          <Line
            yAxisId="stretch"
            type="monotone"
            dataKey="stretch_min"
            name="Stretching"
            stroke="#d4af37"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
      {treat && <TreatmentLine treat={treat} />}
    </div>
  );
}

function TreatmentLine({ treat }) {
  const parts = [];
  if (treat.warm) parts.push(`${treat.warm}× warm`);
  if (treat.cool) parts.push(`${treat.cool}× cool`);
  if (treat.both) parts.push(`${treat.both}× both`);
  const dominant = treat.warm > treat.cool ? "#c2410c" : treat.cool > treat.warm ? "#2563eb" : "#7c3aed";
  return (
    <div className="text-[10px] font-semibold pt-1 border-t border-zinc-100 flex items-center gap-1" style={{ color: dominant }}>
      <Thermometer className="w-3 h-3" />
      Treated {treat.count}× · {parts.join(" · ")}
    </div>
  );
}
